import { Alert, Button, Drawer, Grid, Group, List, Modal, Select, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useEffect, useState } from 'react';
import type { ChangeRequestDto, FileRefDto, RequestDetailDto, RequestViewDto } from '@mexp/shared';
import { http } from '@/api/client';
import { FileUploader } from '@/features/files/FileUploader';
import { toBusinessDate } from '@/lib/format';
import { useDrAction } from '../mutations';
import { BdsReviewerSelect, CategorySelect, ChecklistEditor, checklistPayload, ChecklistRow, CountrySelect, DosageFormSelect, ExporterSelect } from './RequestFields';
import { emptyTaskRow, TaskRow, taskRowErrors, TaskRowsEditor, taskRowsToPayload } from './TaskRowsEditor';

/**
 * RA submits the compiled dossier: to BD for review in BDS, or, for a client-portal requisition (which needs no BD
 * review), straight to the client, who can download it at once.
 */
export function SubmitDossierModal({ request, opened, onClose }: { request: RequestDetailDto; opened: boolean; onClose: () => void }) {
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [link, setLink] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (opened) {
      setFiles([]);
      setLink('');
      setLinkLabel('');
      setNotes('');
    }
  }, [opened]);
  const direct = request.origin === 'client';
  const resend = request.bdStatus === 'returned';
  const version = request.releases.length + 1;
  const send = useDrAction(
    () =>
      http.post<RequestViewDto>(`/dossier-requests/${request.id}/${direct ? 'releases' : 'bd-submissions'}`, {
        fileIds: files.map((f) => f.id),
        link: link.trim() ? { url: link.trim(), label: linkLabel.trim() || undefined } : undefined,
        notes: notes.trim() || undefined,
      }),
    direct ? `Released to ${request.exporterName}` : `Sent to ${request.bdReviewer?.name ?? 'BD'} in BDS`,
    onClose,
  );
  const linkInvalid = !!link.trim() && !/^https?:\/\/\S+$/i.test(link.trim());
  // RA can release a client requisition whenever it decides; the window only warns about work not verified yet.
  const unverified = request.stats.total - request.stats.verified;
  const title = direct
    ? version > 1
      ? `Release version ${version} to the client`
      : 'Release the dossier to the client'
    : resend
      ? 'Send the updated dossier to BD (BDS)'
      : 'Send the dossier to BD (BDS)';
  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg">
      <Stack>
        <Text fz="sm" c="dimmed">
          {direct ? (
            <>
              {request.exporterName} can download it in the client portal as soon as you release it. Client requisitions need no BD review; BDS shows
              the release to {request.bdReviewer ? `${request.bdReviewer.name}'s team` : 'every SC-NBD user'}.
            </>
          ) : (
            <>
              {request.bdReviewer?.name}
              {request.bdReviewer?.teamName ? ` (${request.bdReviewer.teamName})` : ''} reviews it in BDS as SC-NBD and approves or returns it there. Tasks are
              locked while it is with BD.
            </>
          )}
        </Text>
        {direct && (request.stats.total === 0 || unverified > 0) && (
          <Alert color="yellow" variant="light" title={request.stats.total === 0 ? 'No task was assigned' : `${unverified} task${unverified === 1 ? ' is' : 's are'} not verified yet`}>
            You can still release it now: the client gets the files and link you add here.
          </Alert>
        )}
        {resend && request.bdReview.returnReason && (
          <Alert color="orange" variant="light" title="BD asked for">
            {request.bdReview.returnReason}
          </Alert>
        )}
        <FileUploader area="bd_dossier" value={files} onChange={setFiles} onBusyChange={setBusy} label="Completed dossier/docs" />
        <Grid gutter="sm">
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <TextInput label="Or a link to it" placeholder="https://…" value={link} onChange={(e) => setLink(e.currentTarget.value)} error={linkInvalid ? 'Enter a full link starting with https://' : undefined} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <TextInput label="Link subject" placeholder="Optional" value={linkLabel} onChange={(e) => setLinkLabel(e.currentTarget.value)} />
          </Grid.Col>
        </Grid>
        <Textarea
          label={direct ? 'Internal note' : 'Notes for BD'}
          description={direct ? 'For Makin staff and BDS. The client does not see it.' : undefined}
          autosize
          minRows={2}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button color={direct ? 'teal' : 'violet'} disabled={busy || linkInvalid || (!files.length && !link.trim())} loading={send.isPending} onClick={() => send.mutate()}>
            {direct ? 'Release to client' : 'Send to BDS'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export function ReviewerModal({ request, opened, onClose }: { request: RequestDetailDto; opened: boolean; onClose: () => void }) {
  const [userId, setUserId] = useState<string | null>(request.bdReviewer?.id ?? null);
  useEffect(() => setUserId(request.bdReviewer?.id ?? null), [opened, request.bdReviewer?.id]);
  // Client requisitions need no BD review: the person is only the BD contact who sees the requisition in BDS.
  const direct = request.origin === 'client';
  const save = useDrAction(() => http.put(`/dossier-requests/${request.id}/bd-reviewer`, { userId }), direct ? 'BD contact updated' : 'SC-NBD reviewer updated', onClose);
  return (
    <Modal opened={opened} onClose={onClose} title={direct ? 'BD contact in BDS' : 'SC-NBD reviewer'}>
      <Stack>
        <Text fz="sm" c="dimmed">
          {direct
            ? "Client requisitions need no BD review and show in BDS on their own: to the client's BD contact's team (set on the exporter's page), or to every SC-NBD user when there is none. Choose someone here to show this requisition to another team."
            : 'BD reviews the compiled dossier in BDS. The request appears for this person and their BDS team.'}
        </Text>
        {request.bdStatus === 'pending_review' && (
          <Alert variant="light" color="violet">
            The dossier is waiting for review in BDS; it moves to the new reviewer's team.
          </Alert>
        )}
        {opened && <BdsReviewerSelect value={userId} onChange={setUserId} label={direct ? 'BD contact (BDS)' : undefined} />}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!userId || userId === request.bdReviewer?.id} loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * RA assigns tasks. Once a client-portal dossier is released, every new task answers one of the client's open queries
 * (`queryId` preselects it).
 */
export function AddTasksModal({ request, queryId, opened, onClose }: { request: RequestDetailDto; queryId?: string | null; opened: boolean; onClose: () => void }) {
  const [rows, setRows] = useState<TaskRow[]>([emptyTaskRow()]);
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(0);
  const released = request.origin === 'client' && request.releases.length > 0;
  const openQueries = request.changeRequests.filter((c) => c.open);
  const [query, setQuery] = useState<string | null>(null);
  useEffect(() => {
    if (opened) {
      setRows([emptyTaskRow()]);
      setShowErrors(false);
      setQuery(queryId ?? (openQueries.length === 1 ? (openQueries[0]?.id ?? null) : null));
    }
    // Reset when the modal opens; the open queries come with the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, queryId]);
  const chosen = openQueries.find((q) => q.id === query) ?? null;
  const add = useDrAction(
    () => http.post(`/dossier-requests/${request.id}/work-items`, { items: taskRowsToPayload(rows), queryId: released ? (query ?? undefined) : undefined }),
    'Tasks assigned; each person has been notified',
    onClose,
  );
  return (
    <Modal opened={opened} onClose={onClose} title={chosen ? `Assign tasks for Query ${chosen.number} · ${request.code}` : `Assign tasks · ${request.code}`} size="xl">
      <Stack>
        {released && (
          <Stack gap={6}>
            <Select
              label="For client query"
              description="The dossier is released: new tasks answer one of the client's open queries."
              placeholder="Choose the query"
              data={openQueries.map((q) => ({ value: q.id, label: `Query ${q.number}` }))}
              value={query}
              onChange={setQuery}
              allowDeselect={false}
              withAsterisk
              maw={320}
              error={showErrors && !query ? 'Choose the query these tasks are for' : undefined}
            />
            {chosen && (
              <Text fz="sm" c="dimmed" style={{ whiteSpace: 'pre-wrap' }} lineClamp={3}>
                “{chosen.comment}”
              </Text>
            )}
          </Stack>
        )}
        <TaskRowsEditor rows={rows} onChange={setRows} requiredDocs={request.requiredDocs} showErrors={showErrors} onBusyChange={(b) => setBusy((n) => n + (b ? 1 : -1))} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!rows.length || busy > 0}
            loading={add.isPending}
            onClick={() => {
              if (rows.some((r) => taskRowErrors(r).length) || (released && !query)) setShowErrors(true);
              else add.mutate();
            }}
          >
            Assign {rows.length > 1 ? `${rows.length} tasks` : 'task'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * RA asks the client to explain the requisition (or one of their open queries) more clearly, or to send reference
 * documents, before starting the work. `queryId` preselects the query.
 */
export function AskClientModal({ request, queryId, opened, onClose }: { request: RequestDetailDto; queryId?: string | null; opened: boolean; onClose: () => void }) {
  const [about, setAbout] = useState<string>('requisition');
  const [question, setQuestion] = useState('');
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const openQueries = request.changeRequests.filter((c) => c.open);
  useEffect(() => {
    if (opened) {
      setAbout(queryId ?? 'requisition');
      setQuestion('');
      setFiles([]);
      setShowErrors(false);
    }
  }, [opened, queryId]);
  const ask = useDrAction(
    () =>
      http.post<RequestViewDto>(`/dossier-requests/${request.id}/clarifications`, {
        question: question.trim(),
        queryId: about === 'requisition' ? undefined : about,
        fileIds: files.map((f) => f.id),
      }),
    `Sent to ${request.exporterName}; they answer in the client portal`,
    onClose,
  );
  return (
    <Modal opened={opened} onClose={onClose} title="Ask the client" size="lg">
      <Stack>
        {openQueries.length > 0 && (
          <Select
            label="About"
            data={[{ value: 'requisition', label: 'The requisition' }, ...openQueries.map((q) => ({ value: q.id, label: `Query ${q.number}` }))]}
            value={about}
            onChange={(v) => setAbout(v ?? 'requisition')}
            allowDeselect={false}
            maw={320}
          />
        )}
        <Textarea
          label="What should the client clarify?"
          placeholder="For example: which batches need a COA, and in which format?"
          withAsterisk
          autosize
          minRows={3}
          value={question}
          onChange={(e) => setQuestion(e.currentTarget.value)}
          error={showErrors && !question.trim() ? 'Write what the client should clarify' : undefined}
        />
        <FileUploader area="dr_reference" value={files} onChange={setFiles} onBusyChange={setBusy} label="Files for the client (optional), e.g. an example of the format" />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={busy}
            loading={ask.isPending}
            onClick={() => {
              if (!question.trim()) setShowErrors(true);
              else ask.mutate();
            }}
          >
            Send to the client
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** RA answers a client query: a reply and the corrected documents. The released dossier stays as it is. */
export function ComplianceModal({ request, query, onClose }: { request: RequestDetailDto; query: ChangeRequestDto | null; onClose: () => void }) {
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  useEffect(() => {
    setComment('');
    setFiles([]);
    setShowErrors(false);
  }, [query?.id]);
  const lastOpen = request.changeRequests.filter((c) => c.open).length === 1;
  const send = useDrAction(
    () => http.post<RequestViewDto>(`/dossier-requests/${request.id}/queries/${query?.id}/compliance`, { comment: comment.trim(), fileIds: files.map((f) => f.id) }),
    `Compliance on Query ${query?.number} sent to ${request.exporterName}`,
    onClose,
  );
  return (
    <Modal opened={!!query} onClose={onClose} title={query ? `Compliance on Query ${query.number}` : ''} size="lg">
      {query && (
        <Stack>
          <Alert color="grape" variant="light" title={`${query.requestedBy.name} (client) asked`}>
            <Text fz="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {query.comment}
            </Text>
          </Alert>
          <Textarea
            label="Reply to the client"
            withAsterisk
            autosize
            minRows={3}
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            error={showErrors && !comment.trim() ? 'Write your reply to the query' : undefined}
          />
          <FileUploader area="bd_dossier" value={files} onChange={setFiles} onBusyChange={setBusy} label="Corrected documents" />
          {/* <Text fz="sm" c="dimmed">
            {request.exporterName} sees your reply and these files on the dossier page as soon as you send them. The released dossier stays as it is.
            {lastOpen ? ' This is the last open query, so the dossier goes back to Ready to download.' : ''}
          </Text> */}
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="teal"
              disabled={busy}
              loading={send.isPending}
              onClick={() => {
                if (!comment.trim()) setShowErrors(true);
                else send.mutate();
              }}
            >
              Send compliance
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export function AddReferenceFilesModal({ request, opened, onClose }: { request: RequestDetailDto; opened: boolean; onClose: () => void }) {
  const [files, setFiles] = useState<FileRefDto[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => setFiles([]), [opened]);
  const add = useDrAction(() => http.post(`/dossier-requests/${request.id}/reference-files`, { fileIds: files.map((f) => f.id) }), 'Reference files added', onClose);
  return (
    <Modal opened={opened} onClose={onClose} title="Add reference files" size="lg">
      <Stack>
        <FileUploader area="dr_reference" value={files} onChange={setFiles} onBusyChange={setBusy} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!files.length || busy} loading={add.isPending} onClick={() => add.mutate()}>
            Add
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** A request from before the generic and brand names were asked has only its product's name: it starts as the brand name. */
const namesOf = (request: RequestDetailDto) => ({
  genericName: request.genericName ?? '',
  brandName: request.brandName ?? (request.genericName ? '' : request.productName),
});

export function EditRequestDrawer({ request, opened, onClose }: { request: RequestDetailDto; opened: boolean; onClose: () => void }) {
  const [values, setValues] = useState({
    exporterId: request.exporterId,
    country: request.country as string | null,
    ...namesOf(request),
    packSize: request.packSize ?? '',
    category: request.category,
    dosageForm: request.dosageForm,
    requiredBy: request.requiredBy,
    notes: request.notes ?? '',
  });
  const [checklist, setChecklist] = useState<ChecklistRow[]>([]);
  useEffect(() => {
    if (!opened) return;
    setValues({
      exporterId: request.exporterId,
      country: request.country,
      ...namesOf(request),
      packSize: request.packSize ?? '',
      category: request.category,
      dosageForm: request.dosageForm,
      requiredBy: request.requiredBy,
      notes: request.notes ?? '',
    });
    setChecklist(request.requiredDocs.map((d) => ({ key: d.key, savedKey: d.key, name: d.name, required: d.required, notes: d.notes ?? '' })));
  }, [opened, request]);
  const save = useDrAction(
    () =>
      http.patch(`/dossier-requests/${request.id}`, {
        exporterId: values.exporterId ?? undefined,
        country: values.country ?? undefined,
        genericName: values.genericName.trim() || null,
        brandName: values.brandName.trim() || null,
        packSize: values.packSize.trim() || null,
        category: values.category,
        dosageForm: values.dosageForm,
        requiredBy: values.requiredBy,
        notes: values.notes.trim() || null,
        requiredDocs: checklistPayload(checklist),
      }),
    'Request updated',
    onClose,
  );
  const set = <K extends keyof typeof values>(key: K) => (value: (typeof values)[K]) => setValues((v) => ({ ...v, [key]: value }));
  // Required once the request has one; an older request needs at least a name to be called by.
  const genericRequired = !!request.genericName;
  const named = genericRequired ? !!values.genericName.trim() : !!(values.genericName.trim() || values.brandName.trim());
  const linked = checklist.filter((c) => c.savedKey).length;
  return (
    <Drawer opened={opened} onClose={onClose} title={`Edit ${request.code}`} size="xl">
      <Stack>
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <ExporterSelect value={values.exporterId} onChange={set('exporterId')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <CountrySelect value={values.country} onChange={set('country')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label="Generic name"
              value={values.genericName}
              onChange={(e) => set('genericName')(e.currentTarget.value)}
              withAsterisk={genericRequired}
              error={genericRequired && !values.genericName.trim() ? 'Enter the generic name' : undefined}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput label="Brand name" placeholder="Optional" value={values.brandName} onChange={(e) => set('brandName')(e.currentTarget.value)} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput label="Pack size" value={values.packSize} onChange={(e) => set('packSize')(e.currentTarget.value)} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <CategorySelect value={values.category} onChange={set('category')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DosageFormSelect value={values.dosageForm} onChange={set('dosageForm')} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <DateInput
              label={request.origin === 'client' ? 'Date given to the client' : 'Needed by'}
              description={
                request.origin === 'client' ? 'The client sees it as the expected date and is told when it is set or moves' : 'When BD needs the documents'
              }
              placeholder="Optional"
              clearable
              valueFormat="DD MMM YYYY"
              minDate={new Date()}
              value={values.requiredBy}
              onChange={(v) => set('requiredBy')(toBusinessDate(v))}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <Textarea label="Notes" autosize minRows={2} value={values.notes} onChange={(e) => set('notes')(e.currentTarget.value)} />
          </Grid.Col>
        </Grid>
        <Text fw={600}>Documents needed</Text>
        {linked > 0 && (
          <Text fz="xs" c="dimmed">
            Removing a checklist item does not remove tasks already linked to it.
          </Text>
        )}
        <ChecklistEditor rows={checklist} onChange={setChecklist} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!named || !values.country || !values.exporterId} loading={save.isPending} onClick={() => save.mutate()}>
            Save changes
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

export function BlockerList({ blockers }: { blockers: string[] }) {
  return (
    <List size="sm" spacing={2}>
      {blockers.map((b) => (
        <List.Item key={b}>{b}</List.Item>
      ))}
    </List>
  );
}
